import React, { useEffect, useState } from "react";
import {
    Table,
    Button,
    Typography,
    Modal,
    Form,
    Input,
    Upload,
    message,
    notification,
    Image,
    Space,
    Card,
    Select,
    Tag,
} from "antd";
import { PlusOutlined, UploadOutlined, EditOutlined } from "@ant-design/icons";
import axios from "axios";
import { getIdeas, addIdea, updateIdea } from "../../api/api";
import { cdnUrl } from "../../utils/url";
import moment from "moment";
import Spinner from "../../components/Spinner";
import { siteInfo } from "../../utils/SiteInfo";
import { uploader } from "../../utils/upload";



const { Column } = Table;
const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

const STATUS_MAP = {
    1: { label: "Pending", color: "orange" },
    2: { label: "Reviewed", color: "blue" },
    5: { label: "Shortlisted", color: "purple" },
    9: { label: "Best", color: "green" },
};

export default function CrmIdeas({ manager_id }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingIdea, setEditingIdea] = useState(null);
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState([]);
    const [filter, setFilter] = useState({ page: 1 });
    const [recalculate, setRecalculate] = useState(0);
    const [viewDesc, setViewDesc] = useState(null);

    useEffect(() => {
        setLoading(true);
        let mFilter = filter ? { ...filter } : {};
        getIdeas((res) => {
            setData(res);
            setLoading(false);
        }, mFilter);
    }, [filter, recalculate, manager_id]);

    const openNotification = (type, messageText, description) => {
        notification[type]({
            message: messageText,
            description: description,
        });
    };

    const handleUploadChange = ({ fileList: newFileList }) => {
        setFileList(newFileList);
    };

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const newFilesToUpload = fileList.filter((file) => file.originFileObj);
            const uploadedImages = newFilesToUpload.length > 0
                ? await uploader(newFilesToUpload, "image", "administration")
                : [];

            const existingImages = [];
            for (const file of fileList) {
                if (!file.originFileObj) {
                    if (file._id) {
                        existingImages.push(file._id);
                    } else if (typeof file === "string") {
                        existingImages.push(file);
                    }
                }
            }

            const finalImages = [...existingImages, ...uploadedImages];

            const ideaData = {
                title: values.title,
                description: values.description,
                images: finalImages,
                status: values.status || 1,
            };

            if (editingIdea) {
                updateIdea(editingIdea._id, ideaData, (success) => {
                    setLoading(false);
                    if (success) {
                        openNotification("success", "Success", "Idea updated successfully!");
                        setModalOpen(false);
                        form.resetFields();
                        setFileList([]);
                        setEditingIdea(null);
                        setRecalculate((prev) => prev + 1);
                    } else {
                        openNotification("error", "Error", "Failed to update idea.");
                    }
                });
            } else {
                addIdea(ideaData, (success) => {
                    setLoading(false);
                    if (success) {
                        openNotification("success", "Success", "Idea added successfully!");
                        setModalOpen(false);
                        form.resetFields();
                        setFileList([]);
                        setRecalculate((prev) => prev + 1);
                    } else {
                        openNotification("error", "Error", "Failed to add idea.");
                    }
                });
            }
        } catch (error) {
            console.error(error);
            setLoading(false);
            openNotification("error", "Error", "An error occurred while saving the idea.");
        }
    };

    const handleEdit = (record) => {
        setEditingIdea(record);
        form.setFieldsValue({
            title: record.title,
            description: record.description,
            status: record.status || 1,
        });

        const existingFiles = (record.images || []).map((img, idx) => {
            const src = typeof img === "object" && img.url ? img.url : (typeof img === "string" ? (img.startsWith("http") ? img : `${cdnUrl}/${img}`) : "");
            return {
                uid: `existing-${idx}`,
                name: `Image ${idx + 1}`,
                status: "done",
                url: src,
                _id: typeof img === "object" ? img._id : img,
            };
        });
        setFileList(existingFiles);
        setModalOpen(true);
    };

    return (
        <div >
            <div
                style={{
                    display: "flex",
                    marginBottom: "1rem",
                    marginTop: 8,
                    backgroundColor: "#fff",
                    justifyContent: "space-between",
                    padding: 12,
                    alignItems: "center",
                    borderRadius: 6,
                    flexWrap: "wrap",
                    gap: "10px",
                }}
            >
                <Title level={5} style={{ margin: 0, display: "flex", alignItems: "center" }}>
                    CRM Ideas
                    {data?.count !== undefined && (
                        <Tag color="magenta" style={{ marginLeft: 8 }}>
                            {data.count}
                        </Tag>
                    )}
                </Title>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        style={{ marginLeft: "10px" }}
                        onClick={() => {
                            setEditingIdea(null);
                            setFileList([]);
                            form.resetFields();
                            setModalOpen(true);
                        }}
                    >
                        Add Idea
                    </Button>
                </div>
            </div>

            <div style={{
                display: 'flex',
                flexWrap: "wrap",
                gap: "10px",
                marginBottom: "1rem",
            }}>
                {[
                    { title: "All", value: undefined },
                    { title: "Pending", value: 1 },
                    { title: "Reviewed", value: 2 },
                    { title: "Shortlisted", value: 5 },
                    { title: "Best", value: 9 },
                ].map((el) => (
                    <div
                        key={el.title}
                        onClick={() => {
                            let prev = { ...filter, page: 1 };
                            if (el.value === undefined) {
                                delete prev.status;
                            } else {
                                prev.status = el.value;
                            }
                            setFilter(prev);
                        }}
                        style={{
                            padding: "0.5rem 1rem",
                            cursor: "pointer",
                            backgroundColor:
                                filter?.status === el.value
                                    ? siteInfo.navColor
                                    : "#fff",
                            color:
                                filter?.status === el.value ? "#fff" : "#000",
                            borderRadius: 4,
                            fontSize: 13,
                            fontWeight: filter?.status === el.value ? "bold" : "normal",
                        }}
                    >
                        {el.title}
                    </div>
                ))}
            </div>

            {loading && !data ? (
                <Spinner />
            ) : data && data.docs ? (
                <Table
                    dataSource={data.docs}
                    rowKey={(record) => record._id}
                    pagination={{
                        current: filter?.page || 1,
                        pageSize: data?.limit || 10,
                        total: data?.totalDocs || 0,
                        showSizeChanger: false,
                    }}
                    onChange={(page) => {
                        setFilter({ ...filter, page: page.current });
                    }}
                >
                    <Column
                        title="Date"
                        dataIndex="createdAt"
                        key="createdAt"
                        render={(text) => (
                            <Text>
                                {moment(text).format("DD MMM YYYY, hh:mm A")}
                            </Text>
                        )}
                    />
                    <Column
                        title="Title"
                        dataIndex="title"
                        key="title"
                        render={(text) => <Text style={{ fontWeight: "bold" }}>{text}</Text>}
                    />
                    <Column
                        title="Description"
                        dataIndex="description"
                        key="description"
                        width={200}
                        render={(text) => {
                            if (!text) return <Text type="secondary">-</Text>;
                            if (text.length > 60) {
                                return (
                                    <div
                                        onClick={() => setViewDesc(text)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        {text.slice(0, 60)}...{" "}
                                        <span style={{ color: siteInfo.navColor || "#1890ff", fontWeight: "bold" }}>
                                            More
                                        </span>
                                    </div>
                                );
                            }
                            return <span>{text}</span>;
                        }}
                    />
                    <Column
                        title="Images"
                        dataIndex="images"
                        key="images"
                        align="center"
                        render={(images) => (
                            <Space size="small">
                                {images && images.map((img, idx) => {
                                    const src = typeof img === "object" && img.url ? img.url : (typeof img === "string" ? (img.startsWith("http") ? img : `${cdnUrl}/${img}`) : "");
                                    return (
                                        <Image
                                            key={idx}
                                            src={src}
                                            alt="Idea attachment"
                                            style={{
                                                width: 50,
                                                height: 50,
                                                objectFit: "cover",
                                                borderRadius: 4,
                                                border: "1px solid #ddd",
                                            }}
                                            fallback={require("../../assets/imageFallback.svg").default}
                                        />
                                    );
                                })}
                            </Space>
                        )}
                    />
                    <Column
                        title="Status"
                        dataIndex="status"
                        key="status"
                        align="center"
                        render={(status) => {
                            const info = STATUS_MAP[status] || { label: "Pending", color: "orange" };
                            return <Tag color={info.color}>{info.label}</Tag>;
                        }}
                    />
                    <Column
                        title="Action"
                        key="action"
                        align="center"
                        render={(_, record) => (
                            <Button
                                type="primary"
                                disabled={record?.status !== 1}
                                onClick={() => handleEdit(record)}
                            >
                                Edit
                            </Button>

                        )}
                    />
                </Table>
            ) : (
                <Card>
                    <Text>No ideas found.</Text>
                </Card>
            )}

            <Modal
                title={editingIdea ? "Edit Idea" : "Add New Idea"}
                open={modalOpen}
                onCancel={() => {
                    setModalOpen(false);
                    form.resetFields();
                    setFileList([]);
                    setEditingIdea(null);
                }}
                footer={null}
                width={600}
            >
                <Form
                    style={{
                        marginTop: 16
                    }}
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    initialValues={{ title: "", description: "", status: 1 }}
                >
                    <Form.Item
                        name="title"
                        label="Title"
                        rules={[{ required: true, message: "Please input the title of the idea!" }]}
                    >
                        <Input placeholder="Enter idea title" />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Description"
                        rules={[{ required: true, message: "Please input the description!" }]}
                    >
                        <TextArea rows={6} placeholder="Enter idea description" />
                    </Form.Item>

                    {/* {editingIdea && (
                        <Form.Item
                            name="status"
                            label="Status"
                            rules={[{ required: true, message: "Please select status!" }]}
                        >
                            <Select placeholder="Select status">
                                <Option value={1}>Pending</Option>
                                <Option value={2}>Reviewed</Option>
                                <Option value={5}>Shortlisted</Option>
                                <Option value={9}>Best</Option>
                            </Select>
                        </Form.Item>
                    )} */}

                    <Form.Item label="Images">
                        <Upload
                            listType="picture"
                            fileList={fileList}
                            onChange={handleUploadChange}
                            beforeUpload={() => false}
                            multiple
                            accept="image/*"
                        >
                            <Button icon={<UploadOutlined />}>Select Images</Button>
                        </Upload>
                    </Form.Item>

                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                        <Button onClick={() => {
                            setModalOpen(false);
                            setEditingIdea(null);
                        }}>Cancel</Button>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            Submit
                        </Button>
                    </div>
                </Form>
            </Modal>

            <Modal
                title="Description"
                open={!!viewDesc}
                onCancel={() => setViewDesc(null)}
                footer={[
                    <Button key="close" onClick={() => setViewDesc(null)}>
                        Close
                    </Button>
                ]}
            >
                <div style={{ whiteSpace: "pre-wrap", maxHeight: "60vh", overflowY: "auto" }}>
                    {viewDesc}
                </div>
            </Modal>
        </div>
    );
}
